import React, {
  useCallback,
  useState,
} from "react";

import "./ParticipantsPanelV2.css";
import "./RoomPanelShared.css";
import { Button, cn, toast } from "../../../styles/facemeet-ui";
import { logger } from "../../../log/logger";
import { IconX, IconSearch, IconHand, IconBan } from "./panel_Icons_shared";

/* Inline icons used by the new participant toggles. We deliberately
   don't pull from panel_Icons_shared so this file is self-contained
   for the redesign — older shared icons stayed at their previous
   weights/sizes which looked off next to the toggle switch. */
const IconMic = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
    <path d="M19 10v1a7 7 0 0 1-14 0v-1" /><line x1="12" y1="18" x2="12" y2="22" />
  </svg>
);
const IconCam = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 7a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
    <path d="m16 10 5-3v10l-5-3" />
  </svg>
);
const IconChat = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const IconStar = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">
    <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26 12,2" />
  </svg>
);

/* Self-contained toggle component for the per-participant action grid.
   - icon: the lead-in glyph (mic, cam, chat, star, …)
   - label: short noun ("Mic", "Camera", "Chat", "Co-host")
   - state: the freeform-text status shown after the label
           ("On", "Off", "Allowed", "Banned", "Granted", "Yes", "No", …)
   - on: drives the switch color/position (true = blue + thumb right)
   - disabled: greyed + non-interactive
   - hint: title attribute for the row */
function PpToggle({ icon, label, state, on, disabled, onClick, hint }) {
  return (
    <button
      type="button"
      className={[
        "fm-pp-toggle",
        on ? "fm-pp-toggle--on" : "fm-pp-toggle--off",
        disabled ? "fm-pp-toggle--disabled" : "",
      ].filter(Boolean).join(" ")}
      onClick={disabled ? undefined : onClick}
      disabled={!!disabled}
      title={hint}
    >
      <span className="fm-pp-toggle__icon">{icon}</span>
      <span className="fm-pp-toggle__label">{label}</span>
      <span className="fm-pp-toggle__state">{state}</span>
      <span className="fm-pp-toggle__switch" aria-hidden="true">
        <span className="fm-pp-toggle__thumb" />
      </span>
    </button>
  );
}

export function ParticipantsPanel({
  participants,
  isHost,
  isCoHost,
  roomId,
  room,
  username,
  userMap = {},
  unreadPersonal = 0,
  participantRoles = {},
  handRaisedMap = {},
  activeCameraCount = 0,
  maxCameras = 6,
  groupChatDisabled = false,
  groupChatWhitelist = new Set(),
  onToggleGroupChat = () => {},
  onToggleWhitelist = () => {},
  onClose,
  onLowerHand,
  onAssignCoHost,
  onRemoveCoHost,
  viewerMicPermissions = new Set(),
  onGrantViewerMic = () => {},
  onRevokeViewerMic = () => {},
  bannedFromChat = new Set(),
  onBanFromChat = () => {},
  onUnbanFromChat = () => {},
  // ── New in step11.6-hotfix ────────────────────────────────────
  // isObserver=true puts the panel in READ-ONLY mode (shadow-join
  // admin observer). All toggles and the Kick button are hidden;
  // the list still renders so the observer can see who is present.
  isObserver = false,
  // onKickParticipant(identity) — fires when the host taps the
  // Kick action on a row. Optional: omit to hide the Kick button.
  onKickParticipant,
}) {
  const API_URL = process.env.REACT_APP_SERVER_URL;
  const [searchQuery, setSearchQuery] = useState("");
  const myIdentity = room?.localParticipant?.identity;
  const canControl = isHost || isCoHost;

  const [isMutedAll, setIsMutedAll] = useState(false);
  const [localMuteState, setLocalMuteState] = useState({});
  const [localCameraState, setLocalCameraState] = useState({});
  const [kickConfirmId, setKickConfirmId] = useState(null);
  const [highlightId, setHighlightId] = useState(null);

  const scrollToParticipant = useCallback((id) => {
    const row = document.querySelector(`[data-participant-id="${id}"]`);
    if (row) {
      row.scrollIntoView({ behavior: "smooth", block: "nearest" });
      setHighlightId(id);
      setTimeout(() => setHighlightId(null), 1800);
    }
  }, []);

  /* ── Publish helper (with retry) ──────────────────────────────── */
  const publish = async (payload, label = payload?.type || "data") => {
    if (!room) { logger.warn(`[panel-publish:${label}] no room — dropped`); return false; }
    const tryOnce = async () => {
      if (room.state !== "connected") {
        logger.warn(`[panel-publish:${label}] room state=${room.state}, deferring`);
        return false;
      }
      try {
        await room.localParticipant.publishData(
          new TextEncoder().encode(JSON.stringify(payload)),
          { reliable: true },
        );
        return true;
      } catch (err) {
        logger.error(`[panel-publish:${label}] FAILED | ${err.message}`);
        return false;
      }
    };
    let ok = await tryOnce();
    if (!ok) {
      await new Promise((r) => setTimeout(r, 250));
      ok = await tryOnce();
    }
    return ok;
  };

  /* ── Mute / cam controls ──────────────────────────────────────── */
  const lockMute   = (id) => publish({ type: "lock-mute",     target: id });
  const unlockMute = (id) => publish({ type: "unlock-mute",   target: id });
  const lockCam    = (id) => publish({ type: "lock-camera",   target: id });
  const unlockCam  = (id) => publish({ type: "unlock-camera", target: id });

  const handleMute   = (id) => { setLocalMuteState((p) => ({ ...p, [id]: true })); lockMute(id); };
  const handleUnmute = (id) => { setLocalMuteState((p) => ({ ...p, [id]: false })); unlockMute(id); };
  const handleCamOff = (id) => { setLocalCameraState((p) => ({ ...p, [id]: true })); lockCam(id); };
  const handleCamOn  = (id) => { setLocalCameraState((p) => ({ ...p, [id]: false })); unlockCam(id); };

  const muteAll = () => {
    if (!isHost) return;
    const toMute = participants.filter(
      (p) => !p.isLocal &&
        (participantRoles[p.identity] === "coHost" || viewerMicPermissions.has(p.identity)),
    );
    if (toMute.length === 0) return;
    const patch = {};
    toMute.forEach((p) => { patch[p.identity] = true; });
    setLocalMuteState((prev) => ({ ...prev, ...patch }));
    toMute.forEach((p) => lockMute(p.identity));
  };

  const handleToggleMuteAll = () => {
    if (!isMutedAll) muteAll();
    setIsMutedAll((v) => !v);
  };

  /* ── Kick ─────────────────────────────────────────────────────── */
  const confirmKick = async (identity) => {
    setKickConfirmId(null);
    const displayName = userMap[identity] || identity;
    try {
      const res = await fetch(`${API_URL}/v2/kick-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          roomId, identity, requesterId: myIdentity, username: displayName,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Couldn't remove participant");
        return;
      }
      await publish({ type: "kick-user", target: identity }, "kick-user");
      toast.success(`${displayName} was removed`);
    } catch (err) {
      logger.error(`[kick] EXCEPTION | target=${identity}`, err);
      toast.error("Couldn't remove participant");
    }
  };

  /* ── Derived lists ────────────────────────────────────────────── */
  const raisedHandsList = Object.entries(handRaisedMap)
    .filter(([id, d]) => d.raised && id !== myIdentity)
    .map(([id, d]) => ({ id, name: d.username || userMap[id] || id }));

  const filteredParticipants = participants
    .filter((p) => p.identity !== myIdentity)
    .filter((p) => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        (userMap[p.identity] || "").toLowerCase().includes(q) ||
        p.identity.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      const rank = (p) => {
        const r = participantRoles[p.identity] || "viewer";
        if (r === "coHost") return 0;
        if (viewerMicPermissions.has(p.identity)) return 1;
        return 2;
      };
      return rank(a) - rank(b);
    });

  const MAX_COHOSTS = 2;
  const coHostCount = Object.values(participantRoles).filter((r) => r === "coHost").length;
  const atCoHostLimit = coHostCount >= MAX_COHOSTS;
  const MAX_VIEWER_MICS = 2;
  const atViewerMicLimit = viewerMicPermissions.size >= MAX_VIEWER_MICS;

  const getRoleBadge = (identity) => {
    const r = participantRoles[identity] || "viewer";
    if (r === "host") return { label: "host", cls: "fm-pp-role--host" };
    if (r === "coHost") return { label: "co-host", cls: "fm-pp-role--cohost" };
    return { label: "viewer", cls: "fm-pp-role--viewer" };
  };

  /* ── Render ───────────────────────────────────────────────────── */
  return (
    <div
      className="fm-pp-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-label="Participants"
    >
      <div className="fm-pp-panel fm-rpanel">
        {/* HEADER */}
        <div className="fm-rpanel__header">
          <div className="fm-rpanel__title-wrap">
            <h3 className="fm-rpanel__title">Participants</h3>
            <span className="fm-rpanel__count">{participants.length}</span>
          </div>
          <div className="fm-rpanel__actions">
            <button
              className="fm-rpanel__icon-btn fm-rpanel__icon-btn--danger"
              onClick={onClose}
              aria-label="Close participants"
            >
              <IconX size={18} />
            </button>
          </div>
        </div>

        {/* SEARCH */}
        <div className="fm-pp-search">
          <div className="fm-pp-search__wrap">
            <span className="fm-pp-search__icon"><IconSearch size={14} /></span>
            <input
              type="text"
              className="fm-pp-search__input"
              placeholder="Search participants…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search participants"
            />
            {searchQuery && (
              <button
                className="fm-pp-search__clear"
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
              >
                <IconX size={12} />
              </button>
            )}
          </div>
        </div>

        {/* BULK ACTIONS (host only) */}
        {isHost && (
          <div className="fm-pp-bulk">
            <Button variant="secondary" size="sm" fullWidth onClick={handleToggleMuteAll}>
              {isMutedAll ? "🎤 Unmute all" : "🔇 Mute all"}
            </Button>
            <Button
              variant={groupChatDisabled ? "danger" : "secondary"}
              size="sm"
              fullWidth
              onClick={() => onToggleGroupChat(!groupChatDisabled)}
            >
              {groupChatDisabled ? "💬 Enable group chat" : "🚫 Disable group chat"}
            </Button>
          </div>
        )}

        {/* VIEWER MIC SLOT STATUS */}
        {isHost && (
          <div className={cn("fm-pp-banner", atViewerMicLimit && "fm-pp-banner--full")}>
            🎙️ Viewer mic slots: {viewerMicPermissions.size} / {MAX_VIEWER_MICS}
            {atViewerMicLimit && " — revoke one to add another"}
          </div>
        )}

        {/* CO-HOST LIMIT */}
        {isHost && atCoHostLimit && (
          <div className="fm-pp-banner fm-pp-banner--warn">
            ⚠️ Co-host limit reached ({MAX_COHOSTS}/{MAX_COHOSTS}) — remove one to add another
          </div>
        )}

        {/* RAISED HANDS */}
        {raisedHandsList.length > 0 && (
          <div className="fm-pp-raised">
            <div className="fm-pp-raised__label">
              <IconHand size={11} /> Raised hands ({raisedHandsList.length})
            </div>
            <div className="fm-pp-raised__list">
              {raisedHandsList.map(({ id, name }) => (
                <div key={id} className="fm-pp-raised__row">
                  <span
                    className="fm-pp-raised__name"
                    title="Jump to participant"
                    onClick={() => scrollToParticipant(id)}
                  >
                    {name}
                  </span>
                  {isHost && (
                    <button
                      className="fm-pp-ctrl fm-pp-ctrl--active"
                      onClick={() => onLowerHand(id)}
                    >
                      Lower
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PARTICIPANT LIST */}
        <div className="fm-pp-list">
          {/* ── You — pinned at top, no controls ───────────────────────── */}
          {myIdentity && !searchQuery && (
            <div className="fm-pp-row" data-participant-id={myIdentity}>
              <div className="fm-pp-row__top">
                <div className="fm-pp-avatar" aria-hidden="true">
                  {(username?.[0] || "?").toUpperCase()}
                </div>
                <div className="fm-pp-row__meta">
                  <div className="fm-pp-row__name-line">
                    <span className="fm-pp-row__name">{username} (you)</span>
                    <span
                      className={`fm-pp-role ${
                        isHost ? "fm-pp-role--host"
                        : isCoHost ? "fm-pp-role--cohost"
                        : "fm-pp-role--viewer"
                      }`}
                    >
                      {isHost ? "host" : isCoHost ? "co-host" : "viewer"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {filteredParticipants.length === 0 && !searchQuery && (
            <div className="fm-pp-empty">
              No one else has joined yet. Share the meeting link to invite people.
            </div>
          )}

          {filteredParticipants.length === 0 && searchQuery && (
            <div className="fm-pp-empty">
              No matches for "{searchQuery}"
            </div>
          )}

          {filteredParticipants.map((p) => {
            const displayName = userMap[p.identity] || p.identity;
            const raised = handRaisedMap[p.identity]?.raised;
            const pRole = participantRoles[p.identity] || "viewer";
            const isPublisher = pRole === "coHost" || pRole === "host";
            const isViewer = pRole === "viewer";
            const targetIsHost = pRole === "host";              // ← NEW
            const cohostBlocked = isCoHost && !isHost && targetIsHost;  // ← NEW
            const badge = getRoleBadge(p.identity);
            const isHighlighted = highlightId === p.identity;
            const isMuted = localMuteState[p.identity] ?? p.isMuted;
            const isVideoOff = localCameraState[p.identity] ?? p.isVideoOff;
            const hasViewerMic = viewerMicPermissions.has(p.identity);
            const isBanned = bannedFromChat.has(p.identity);
            const atCameraCap = activeCameraCount >= maxCameras;

            return (
              <div
                key={p.identity}
                data-participant-id={p.identity}
                className={cn("fm-pp-row", isHighlighted && "fm-pp-row--highlight")}
              >
                <div className="fm-pp-row__top">
                  <div className="fm-pp-avatar" aria-hidden="true">
                    {(displayName[0] || "?").toUpperCase()}
                  </div>

                  <div className="fm-pp-row__meta">
                    <div className="fm-pp-row__name-line">
                      <span className="fm-pp-row__name">{displayName}</span>
                      <span className={cn("fm-pp-role", badge.cls)}>{badge.label}</span>
                    </div>
                    {(isBanned || raised) && (
                      <div className="fm-pp-row__status">
                        {isBanned && (
                          <span className="fm-pp-row__status--ban" title="Muted from chat">
                            <IconBan size={12} />
                          </span>
                        )}
                        {raised && (
                          <span className="fm-pp-row__status--hand" title="Hand raised">
                            <IconHand size={12} />
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {(isObserver || (p.identity && p.identity.startsWith("admin-observer-"))) ? (
                  /* Either the viewer IS the support observer (panel
                     in observer mode), or the ROW IS the support
                     observer (host viewing a meeting being audited).
                     Either way: no controls. The observer is
                     untouchable — kick/mute/promote are all disabled. */
                  null
                ) : canControl && !cohostBlocked && (
                  <div className="fm-pp-toggles">
                    {/* MIC — meaning depends on role:
                        - publisher: mute/unmute the live mic
                        - viewer with grant: also mute/unmute their granted mic
                        - viewer without grant: grant/revoke mic access */}
                    {(isPublisher || hasViewerMic) ? (
                      <PpToggle
                        icon={<IconMic />}
                        label="Mic"
                        state={isMuted ? "Off" : "On"}
                        on={!isMuted}
                        onClick={() => isMuted ? handleUnmute(p.identity) : handleMute(p.identity)}
                      />
                    ) : isHost && isViewer ? (
                      <PpToggle
                        icon={<IconMic />}
                        label="Mic access"
                        state={hasViewerMic ? "Granted" : "None"}
                        on={hasViewerMic}
                        disabled={!hasViewerMic && atViewerMicLimit}
                        hint={!hasViewerMic && atViewerMicLimit ? `Max ${MAX_VIEWER_MICS} viewer mics` : undefined}
                        onClick={() => hasViewerMic ? onRevokeViewerMic(p.identity) : onGrantViewerMic(p.identity)}
                      />
                    ) : null}

                    {/* CAMERA — only for publishers; viewers can't publish video */}
                    {isPublisher && (
                      <PpToggle
                        icon={<IconCam />}
                        label="Camera"
                        state={isVideoOff ? "Off" : "On"}
                        on={!isVideoOff}
                        disabled={isVideoOff && atCameraCap}
                        hint={isVideoOff && atCameraCap ? "Camera limit reached" : undefined}
                        onClick={() => {
                          if (isVideoOff && !atCameraCap) handleCamOn(p.identity);
                          else if (!isVideoOff) handleCamOff(p.identity);
                        }}
                      />
                    )}

                    {/* CHAT — flipped so "on" = allowed to chat. */}
                    <PpToggle
                      icon={<IconChat />}
                      label="Chat"
                      state={isBanned ? "Banned" : "Allowed"}
                      on={!isBanned}
                      onClick={() => isBanned ? onUnbanFromChat(p.identity) : onBanFromChat(p.identity)}
                    />

                    {/* CO-HOST — host-only, not shown on host row. */}
                    {isHost && !targetIsHost && (
                      <PpToggle
                        icon={<IconStar />}
                        label="Co-host"
                        state={pRole === "coHost" ? "Yes" : "No"}
                        on={pRole === "coHost"}
                        disabled={pRole !== "coHost" && atCoHostLimit}
                        hint={pRole !== "coHost" && atCoHostLimit ? `Max ${MAX_COHOSTS} co-hosts` : undefined}
                        onClick={() => pRole === "coHost" ? onRemoveCoHost(p.identity) : onAssignCoHost(p.identity)}
                      />
                    )}

                    {/* CHAT WHITELIST (host + chat disabled) — surfaces only in
                        that narrow mode. */}
                    {isHost && groupChatDisabled && (
                      <PpToggle
                        icon={<IconChat />}
                        label="Chat (override)"
                        state={groupChatWhitelist.has(p.identity) ? "Allowed" : "Blocked"}
                        on={groupChatWhitelist.has(p.identity)}
                        onClick={() => onToggleWhitelist(p.identity)}
                      />
                    )}

                    {/* KICK — destructive action, separated from the toggle
                        list visually and only shown if a handler is wired. */}
                    {typeof onKickParticipant === "function" && (
                      <button
                        type="button"
                        className="fm-pp-kick"
                        onClick={() => onKickParticipant(p.identity)}
                      >
                        Kick
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default ParticipantsPanel;